/**LAGOSSettings — /state/lagos/settings — Admin Workspace */
import React, { useState } from "react";
import { useLagos } from "@/context/LAGOSContext";
import { SectionCard } from "@/components/ds";
import { Settings, Lock, Shield, BarChart2, Globe, Layers, DollarSign, Users, ChevronDown, ChevronUp } from "lucide-react";
import GovernanceEditor from "@/components/GovernanceEditor";

const SECTIONS = [
  {
    id: "platform",
    icon: Settings,
    title: "Platform Configuration",
    color: "#0f172a",
    fields: [
      { key: "Platform",          value: "Lagos State Climate, Circularity & Carbon Platform", editable: false },
      { key: "Version",           value: "v1.0 — 2026 Q1",                                    editable: false },
      { key: "Protocol",          value: "DCI-LAGOS-V1.0",                                    editable: false },
      { key: "Calculation Basis", value: "IPCC 2006 / GHG Protocol / Verra Standards",        editable: false },
      { key: "Report Format",     value: "Institutional grade · Full navy cover · QR-verified",editable: false },
      { key: "Entities Capacity", value: "1,000+ entities (74 seeded for Q1 2026)",            editable: false },
    ],
  },
  {
    id: "methodology",
    icon: BarChart2,
    title: "Methodology Settings",
    color: "#1d4ed8",
    fields: [
      { key: "Grid EF (Scope 2)",  value: "0.43 kgCO₂e/kWh — NERC Nigeria 2022",  editable: true, note: "Requires admin approval to change" },
      { key: "GWP Standard",       value: "AR4 — CH₄ = 25 (CDM / VCS legacy)",    editable: false },
      { key: "Scope 3 Proxy",      value: "0.08 tCO₂e/t material input",            editable: false },
      { key: "Transport EF",       value: "0.21 kgCO₂e/km (diesel vehicle)",        editable: false },
      { key: "Diesel EF",          value: "2.68 kgCO₂e/litre",                     editable: false },
      { key: "LPG EF",             value: "2.98 kgCO₂e/kg",                        editable: false },
    ],
  },
  {
    id: "eco_label",
    icon: Layers,
    title: "Eco Label Parameters",
    color: "#7c3aed",
    fields: [
      { key: "Composite Basis",        value: "Climate (40%) + Circularity (30%) + Confidence (30%)",      editable: false },
      { key: "Platinum Threshold",     value: "Composite ≥82 AND evidence = high/measured",               editable: false },
      { key: "Gold Threshold",         value: "Composite ≥68 + medium-to-high evidence",                  editable: false },
      { key: "Silver Threshold",       value: "Composite ≥53",                                            editable: false },
      { key: "Bronze Threshold",       value: "Composite ≥38",                                            editable: false },
      { key: "Override Policy",        value: "NO MANUAL OVERRIDE — derived only from SSOT calculation",  editable: false },
      { key: "Label Validity Period",  value: "12 months from issue date",                                editable: true, note: "Admin locked — contact DeCarbonIQ" },
    ],
  },
  {
    id: "carbon",
    icon: DollarSign,
    title: "Carbon & Bond Assumptions",
    color: "#059669",
    fields: [
      { key: "VCM Price (Low)",         value: "$10/tCO₂e — floor scenario",                    editable: false },
      { key: "VCM Price (Base)",        value: "$20/tCO₂e — market expectation",                editable: false },
      { key: "VCM Price (High)",        value: "$40/tCO₂e — upside scenario",                  editable: false },
      { key: "Art.6.4 Status",          value: "CONDITIONAL — NCCC sovereign authorization required", editable: false },
      { key: "PoA Status",              value: "1 State PoA — Design Phase",                   editable: false },
      { key: "DSCR Carbon Treatment",   value: "Carbon excluded from base DSCR per ICMA GBP",  editable: false },
      { key: "Blended Bond Coupon",     value: "5.0–7.5% indicative (subject to market)",       editable: false },
    ],
  },
  {
    id: "registry",
    icon: Globe,
    title: "Registry & Programme Settings",
    color: "#0891b2",
    fields: [
      { key: "Active Period",          value: "PER-LAGOS-2026-Q1 (Q1 2026: Jan–Mar)",           editable: false },
      { key: "Coordinating Entity",    value: "Lagos State Government (proposed)",               editable: false },
      { key: "Registry Standard",      value: "Verra VCS + Lagos State Eco-Label Register",    editable: false },
      { key: "QR Verification Base",   value: "decarboniq.com/verify/report/",                  editable: false },
      { key: "Audit Trail",            value: "SHA-256 hash per calculation run — immutable",   editable: false },
      { key: "Public Disclosure",      value: "Eco-labels publicly verifiable via QR",          editable: false },
    ],
  },
  {
    id: "evidence",
    icon: Shield,
    title: "Evidence Governance Settings",
    color: "#dc2626",
    fields: [
      { key: "approval_rate_weight",  value: "0.35 (35%)",  editable: true,  note: "Admin only — changes version-snapshot the config" },
      { key: "checklist_rate_weight", value: "0.40 (40%)",  editable: true,  note: "Admin only" },
      { key: "critical_rate_weight",  value: "0.25 (25%)",  editable: true,  note: "Admin only" },
      { key: "VCM Ready Threshold",   value: "Score ≥65 · ≥3 approved · critical ≥80%", editable: true, note: "Admin only" },
      { key: "Art.6.4 Threshold",     value: "Score ≥80 · ≥4 approved · critical 100%", editable: true, note: "Admin only" },
      { key: "Green Bond Threshold",  value: "Score ≥70 · ≥3 approved · critical ≥80%", editable: true, note: "Admin only" },
      { key: "KYC Expiry",            value: "365 days",    editable: true,  note: "Admin only" },
      { key: "Utility Bill Expiry",   value: "90 days",     editable: true,  note: "Admin only" },
      { key: "Meter Data Expiry",     value: "30 days",     editable: true,  note: "Admin only" },
      { key: "Governance API",        value: "PUT /api/lagos/vault/governance/config", editable: false },
      { key: "Version Snapshots",     value: "Immutable — every change creates a snapshot", editable: false },
    ],
  },
  {
    id: "access",
    icon: Users,
    title: "User & Access Controls",
    color: "#d97706",
    fields: [
      { key: "Platform Role",         value: "Lagos State — restricted workspace",     editable: false },
      { key: "Admin Access",          value: "admin@decarboniq.com",                  editable: false },
      { key: "Lagos State Admin",     value: "admin@lagosstate.gov",                  editable: false },
      { key: "Cross-workspace Guard", value: "403 enforced for all other roles",       editable: false },
      { key: "Data Isolation",        value: "Strict — no NB/NIRSAL/Dangote leakage", editable: false },
      { key: "MFA",                   value: "JWT-based — Phase 2 MFA upgrade recommended", editable: false },
    ],
  },
];

function SettingsSection({ section, expanded, toggle }) {
  const Icon = section.icon;
  return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors"
        onClick={toggle}
        data-testid={`settings-section-${section.id}`}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${section.color}15` }}>
            <Icon size={15} style={{ color: section.color }} />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-800">{section.title}</p>
            <p className="text-[10px] text-slate-400">
              {section.id === "evidence" ? "Live rules editor — version-controlled" : `${section.fields?.length || 0} parameters`}
            </p>
          </div>
        </div>
        {expanded ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
      </button>
      {expanded && (
        <div className="border-t border-slate-100 px-5 pb-4 pt-3">
          {section.id === "evidence" ? (
            <GovernanceEditor />
          ) : (
            <div className="space-y-0">
              {(section.fields || []).map(f => (
                <div key={f.key} className="flex items-start justify-between py-2.5 border-b border-slate-50 gap-4">
                  <div className="min-w-[160px] flex items-center gap-1.5">
                    <p className="text-xs text-slate-500 font-medium">{f.key}</p>
                    {!f.editable && <Lock size={9} className="text-slate-300 flex-shrink-0" />}
                  </div>
                  <div className="flex-1 text-right">
                    <p className="text-xs font-semibold text-slate-700">{f.value}</p>
                    {f.note && <p className="text-[9px] text-amber-600 mt-0.5">{f.note}</p>}
                  </div>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold flex-shrink-0 ${f.editable ? "bg-blue-50 text-blue-600" : "bg-slate-100 text-slate-400"}`}>
                    {f.editable ? "Editable" : "Locked"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function LAGOSSettings() {
  const { selectedPeriod, periods } = useLagos();
  const [expanded, setExpanded] = useState({ platform: true });
  const toggle = id => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="p-6 lg:p-8 bg-[#F8FAFC] min-h-screen space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded">Admin Workspace</span>
          <h1 className="text-2xl font-black text-slate-900 mt-1.5">Platform Settings</h1>
          <p className="text-xs text-slate-400 mt-0.5">
            Configuration · Methodology · Eco Label · Carbon Assumptions · Registry · Access Controls
          </p>
        </div>
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-2">
          <Shield size={13} className="text-emerald-600" />
          <div>
            <p className="text-[9px] text-slate-400 uppercase tracking-wide font-bold">Active Period</p>
            <p className="text-xs font-bold text-emerald-700">{selectedPeriod || "—"}</p>
          </div>
        </div>
      </div>

      {/* Platform status banner */}
      <div className="bg-[#0f172a] rounded-xl p-4 text-white">
        <p className="text-[9px] font-bold uppercase tracking-widest opacity-40 mb-3">Platform Status</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            ["Platform Health",    "Operational",           "#34d399"],
            ["SSOT Status",        "Active — v1.0",         "#34d399"],
            ["Reporting Period",   selectedPeriod || "—",    "#94a3b8"],
            ["Periods Available",  String(periods.length),  "#94a3b8"],
          ].map(([k, v, c]) => (
            <div key={k}>
              <p className="text-[9px] opacity-40 uppercase tracking-wider">{k}</p>
              <p className="text-sm font-black mt-0.5" style={{ color: c }}>{v}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-3">
        {SECTIONS.map(s => (
          <SettingsSection
            key={s.id}
            section={s}
            expanded={!!expanded[s.id]}
            toggle={() => toggle(s.id)}
          />
        ))}
      </div>

      {/* Admin note */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-2">
        <Lock size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-bold text-amber-800">Locked Parameters</p>
          <p className="text-[10px] text-amber-700 mt-1">
            Parameters marked "Admin Locked" require a DeCarbonIQ platform administrator to modify.
            Changes to emission factors, GWP standards, or eco-label thresholds require a version
            increment and re-calculation of all affected entities.
          </p>
        </div>
      </div>
    </div>
  );
}
